var DomainException = require('../DomainException');
const AggregateRoot = require('../ddd/AggregateRoot');
const _ = require('../../lodash');

class AccountSetting extends AggregateRoot {
   constructor({ user, logger, sequenceProvider }) {
      super({ user, logger });

      this.sequenceProvider = sequenceProvider;
   }

   get agt() {
      return 'AccountSetting';
   }

   /* =================== */

   addTaskType({ name }) {
      this.logger.debug('AccountSetting.addTaskType =>', arguments);

      this.guardUniqueName(name);

      var p = { name };
      p.no = this.getNextTaskTypeNo();
      p.order = this.getNextTaskTypeOrderNo();

      this.apply('TASKTYPE_ADDED', p);

      var taskType = {
         no: p.no,
         name: p.name,
         order: p.order,
         deleted: false
      };

      this._properties.taskTypes.push(taskType);

      return taskType;
   }

   updateTaskType({ noTaskType, name }) {
      let taskType = this.guardMissingTaskTypeNo(noTaskType);

      this.guardUniqueName(name, noTaskType);

      const before = _.pick(taskType, ['name']);

      var p = { after: { name }, before, no: taskType.no };

      this.apply('TASKTYPE_UPDATED', p);

      taskType.name = p.after.name;
   }

   deleteTaskType({ noTaskType }) {
      let taskType = this.guardMissingTaskTypeNo(noTaskType);

      if (noTaskType == 0) {
         throw new DomainException(
            "Cannot delete the 'Unspecified' type, as it is a system type. Consider renaming if it doesn't suit your needs."
         );
      }

      var p = { no: noTaskType };

      this.apply('TASKTYPE_DELETED', p);

      taskType.deleted = true;
      let idx = _.findIndex(this._properties.taskTypes, { no: taskType.no });
      this._properties.taskTypes.splice(idx, 1);
      this._properties.deletedTaskTypes.push(taskType);
   }

   getAllTaskTypes() {
      return this._properties.taskTypes.concat(this._properties.deletedTaskTypes);
   }

   guardMissingTaskTypeNo(noTaskType, message) {
      message = message || 'No such TaskType exists.';
      var i = _.find(this._properties.taskTypes, { no: noTaskType });
      if (!i) {
         throw new DomainException(message);
      }
      return i;
   }

   guardUniqueName(name, noIgnore, message) {
      message = message || 'An TaskType with that name already exists.';

      var sl = _.find(this._properties.taskTypes, (x) => {
         return x.name === name;
      });

      if (sl && sl.no !== noIgnore) {
         throw new DomainException(message);
      }
   }

   getNextTaskTypeNo() {
      const allTypes = this.getAllTaskTypes();

      if (allTypes.length == 0) {
         return 1;
      }

      var sorted = _.sortBy(allTypes, (ag) => {
         return ag.no;
      });

      return sorted.reverse()[0].no + 1;
   }

   getNextTaskTypeOrderNo() {
      const allTypes = this.getAllTaskTypes();

      if (allTypes.length == 0) {
         return 1;
      }

      var sorted = _.sortBy(allTypes, (ag) => {
         return ag.order;
      });

      return sorted.reverse()[0].no + 1;
   }

   /* --- Issues --- */

   addPriority({ name, color, resolutionTargetMins = null }) {
      this.logger.debug('AccountSetting.addPriority =>', arguments);

      this.guardUniqueIssueName(name);
      this.guardResolutionTargetMins(resolutionTargetMins);

      var p = { name, color, resolutionTargetMins };
      p.no = this.getNextPriorityNo();
      p.order = this.getNextPriorityOrderNo();
      p.color = color;

      this.apply('PRIORITY_ADDED', p);

      var priority = {
         no: p.no,
         name: p.name,
         color: p.color,
         order: p.order,
         resolutionTargetMins: p.resolutionTargetMins,
         deleted: false
      };

      this._properties.priorities.push(priority);

      return priority;
   }

   updatePriority({ noPriority, name, color, resolutionTargetMins = null }) {
      let priority = this.guardMissingPriorityNo(noPriority);

      this.guardUniqueIssueName(name, noPriority);
      this.guardResolutionTargetMins(resolutionTargetMins);

      const before = _.pick(priority, ['name', 'color', 'resolutionTargetMins']);

      var p = { after: { name, color, resolutionTargetMins }, before, no: priority.no };

      this.apply('PRIORITY_UPDATED', p);

      priority.name = p.after.name;
      priority.color = p.after.color;
      priority.resolutionTargetMins = p.after.resolutionTargetMins;
   }

   reorderPriorities({ orderedPriorityNos }) {
      if (!Array.isArray(orderedPriorityNos)) {
         throw new DomainException('Priority order must be an array.');
      }

      const activePriorities = this._properties.priorities;
      const activeNos = activePriorities.map((priority) => priority.no);
      const uniqueNos = _.uniq(orderedPriorityNos);

      if (orderedPriorityNos.length !== activeNos.length || uniqueNos.length !== activeNos.length) {
         throw new DomainException('Priority order must contain every active Priority exactly once.');
      }

      const hasUnknownNo = _.some(orderedPriorityNos, (noPriority) => !_.includes(activeNos, noPriority));
      if (hasUnknownNo) {
         throw new DomainException('Priority order contains an unknown or deleted Priority.');
      }

      const before = _.sortBy(activePriorities, ['order']).map((priority) => priority.no);
      if (_.isEqual(before, orderedPriorityNos)) return;

      const after = orderedPriorityNos.map((noPriority, index) => ({
         no: noPriority,
         order: index + 1
      }));

      this.apply('PRIORITIES_REORDERED', { before, after: orderedPriorityNos });

      _.each(after, (orderedPriority) => {
         const priority = _.find(activePriorities, { no: orderedPriority.no });
         priority.order = orderedPriority.order;
      });

      this._properties.priorities = _.sortBy(activePriorities, ['order']);
   }

   deletePriority({ noPriority }) {
      let priority = this.guardMissingPriorityNo(noPriority);

      var p = { no: noPriority };

      this.apply('PRIORITY_DELETED', p);

      priority.deleted = true;
      let idx = _.findIndex(this._properties.priorities, { no: priority.no });
      this._properties.priorities.splice(idx, 1);
      this._properties.deletedPriorities.push(priority);
   }

   getAllPriorities() {
      return this._properties.priorities.concat(this._properties.deletedPriorities);
   }

   guardMissingAnyPriorityNo(noPriority, message) {
      message = message || 'No such Priority exists.';
      var i = _.find(this.getAllPriorities(), { no: noPriority });
      if (!i) {
         throw new DomainException(message);
      }
      return i;
   }

   guardMissingPriorityNo(noPriority, message) {
      message = message || 'No such Priority exists.';
      var i = _.find(this._properties.priorities, { no: noPriority });
      if (!i) {
         throw new DomainException(message);
      }
      return i;
   }

   guardUniqueIssueName(name, noIgnore, message) {
      message = message || 'A Priority with that name already exists.';

      var sl = _.find(this._properties.priorities, (x) => {
         return x.name === name;
      });

      if (sl && sl.no !== noIgnore) {
         throw new DomainException(message);
      }
   }

   guardResolutionTargetMins(resolutionTargetMins) {
      if (resolutionTargetMins == null) return;
      if (!Number.isInteger(resolutionTargetMins) || resolutionTargetMins <= 0 || resolutionTargetMins > 5256000) {
         throw new DomainException('Resolution target must be a whole number of minutes between 1 and 5256000.');
      }
   }

   getNextPriorityNo() {
      const allFlags = this.getAllPriorities();

      if (allFlags.length == 0) {
         return 1;
      }

      var sorted = _.sortBy(allFlags, (ag) => {
         return ag.no;
      });

      return sorted.reverse()[0].no + 1;
   }

   getNextPriorityOrderNo() {
      const activePriorities = this._properties.priorities;

      if (activePriorities.length == 0) {
         return 1;
      }

      const sorted = _.sortBy(activePriorities, (priority) => priority.order);
      return sorted[sorted.length - 1].order + 1;
   }
}

module.exports = AccountSetting;
